//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useTheme } from '@mui/material/styles';
import {
  Box,
  CardHeader
} from '@mui/material';

// === CONSTANTS ===
import { SUCCESS_CODE } from "constants/general";

// === API ===
import { addTableRow, isFocusedField, onField, onFieldFocus, removeListener, removeTableRow, requestField, requestPreviousFieldFocus, setMainField, updateField } from "api/prokey/prokey.websocket";
import { Pagetype , validate as validate_pagetype  } from './components/Pagetype/Pagetype';
import { Doctype  , validate as validate_doctype   } from './components/Doctype/Doctype';
import { Standard , validate as validate_standard  } from './components/Standard/Standard';
import { Selection, validate as validate_selection } from './components/Selection/Selection';
import { Amount   , validate as validate_amount    } from './components/Amount/Amount';
import { Date     , validate as validate_date      } from './components/Date/Date';

//#endregion

//-----------------------------------------------------------------
export function Field({id}) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const [ data        , setData        ] = useState( {} );
  const [ content     , setContent     ] = useState( '' );
  const [ message     , setMessage     ] = useState( '' );
  const [ reference   , setReference   ] = useState( null );
  const [ has_focus   , setHasFocus    ] = useState( isFocusedField( id ) );

  const header = data.DisplayInfo ? data.DisplayInfo.DisplayHeader : '' ;
  const length_char = data.DisplayInfo ? +data.DisplayInfo.Length : 50;
  const length = data.DisplayInfo ? Math.max(length_char * 12, 20 ) : '100%' ;
  const type   = data.DisplayInfo ?.Type;
  const alt_content       = data.DisplayInfo ? data.DisplayInfo.AltContent      || [] : [];
  const cross_doc_content = data.DisplayInfo ? data.DisplayInfo.CrossDocContent || [] : [];
  const selection_values  = [ ...alt_content, ...cross_doc_content ];

  const is_required      =  data.validationInfo?.isRequired;
  const is_validated     = (data.validationInfo?.isRejected || data.validationInfo?.isAccepted);
  const validation_color =  data.validationInfo?.isAccepted ? theme.palette.success.main : theme.palette.error.main;
  const border_color = is_validated ? validation_color : is_required ? theme.palette.status.review[theme.palette.mode] : theme.palette.status.certain.main;

  const is_visible = data.is_visible;

  let input_element = null;
  let input_validation = () => { return false };

  const setRefNode = useCallback(node => {
    if (node !== null) {
      setReference( {current: node} );
    }
  }, []);

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display   : is_visible ? '' : 'none',
      padding   : '1rem',
      flex      : 'auto',
      maxWidth  : '-webkit-fill-available',
      maxWidth  : '100%',
    },
    header: {
      padding: 0,
      paddingBottom: '0.5rem',
    },
    page_number:{
      paddingRight    : '1rem',     
      fontSize        : '17px',
      marginTop       : '0.7rem',
    },
    content: {
      width: '100%',
    },
    input: {
      borderRadius    : '5px',
      borderStyle     : 'solid',
      borderColor     : border_color,
      fontSize        : '15px',
      width           : length,
      height          : '30px',
      textTransform   : 'uppercase',
      paddingLeft     : '0.5rem',
      maxWidth        : '-webkit-fill-available',
      maxWidth        : '100%',
    },
  }

  const _props = {
    container: {
      sx: _styles.container,
      key: id,
      onClick: () => { setMainField(data); },
    },
    header: {
      sx: _styles.header,
      title: header,      
    },
    content: {
      sx: _styles.content,      
    },
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleField(data) {
      //-----------------------------------------------------------------
      setData(data);

      let value = data.Entries ? data.Entries[0].Content : '';
      setContent( value );
    }

    onField( id, handleField );

    requestField( id );
    return () => {
      removeListener( 'FIELD-' + id, handleField )
    }
  }, [id] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFieldFocus(data) {
      //-----------------------------------------------------------------
      if ( data ) setData( data );
      setHasFocus( true );
      if( !reference ) return;
      reference.current.focus();
      reference.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
    }

    onFieldFocus( id, handleFieldFocus );

    return () => {
      removeListener( 'FIELD-FOCUS-' + id, handleFieldFocus )
    }
  }, [reference] );

  useEffect( () => {
    if( !has_focus || !reference?.current ) return;

    reference.current.select();
    reference.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
  }, [has_focus, reference] );

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onFocus() {
    //-----------------------------------------------------------------
    try {

    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onBlur() {
    //-----------------------------------------------------------------
    try {
      setHasFocus( false );
      setMessage( '' );
    } catch (error) {
      console.warn(error);
    }
  }
  //-----------------------------------------------------------------
  function toBoolean(str) {
    //-----------------------------------------------------------------
    if( str === true ) return true;
    const enabled = ['1', 'true', 'yes', 'ja', 'on'];
    return str && enabled.includes(str.toLowerCase());
  }
  
  //-----------------------------------------------------------------
  function onKeyDown( event ) {
    //-----------------------------------------------------------------
    const is_action_prev = event.key === 'Tab' && event.shiftKey ;
    if( is_action_prev ) {
      event.preventDefault();
      event.stopPropagation();
      requestPreviousFieldFocus( id );
      return;
    }
      
    const ref_content = reference.current.value;

    const is_action_force = event.key === 'Enter' &&  event.ctrlKey  ;
    const is_action_enter = event.key === 'Enter' && !event.ctrlKey  ;
    const is_action_tab   = event.key === 'Tab'   && !event.shiftKey ;

    const force_accept    = data?.validationInfo?.ForceAccept;
    const is_force_empty  = ref_content === '' && force_accept?.toLowerCase() === 'empty';
    const is_force_always = force_accept?.toLowerCase() === 'always';
    const is_forcable     = is_force_always || is_force_empty;
    const is_forced       = is_action_force && is_forcable;

    const is_accepted = is_action_force || is_action_enter || is_action_tab;

    const validation_result_common = validate_common( ref_content );
    const validation_result = is_accepted ? input_validation( ref_content, data.validationInfo ) : {};
    const is_valid = validation_result_common && validation_result?.status === SUCCESS_CODE;

    const has_coords         = data.Entries[0].Bottom > 0;
    const is_coords_required = toBoolean( data?.validationInfo?.RequireCoords );
    const is_coords_failed   = is_coords_required && !has_coords;

    if ( is_action_tab ) {
      // Stop default tab behavior
      event.preventDefault();
    }

    // === VALIDATION ===
    if( is_forced ) {
      setMessage('Field has been force accepted!');

      if( ref_content !== validation_result.content ) setContent( validation_result.content );
      updateField( 'FORCE', id, data.Name, { Content: validation_result.content } );
      return;
    }

    if( is_accepted && is_coords_failed ) {
      setMessage( 'Field requires coordinates!' );
      data.validationInfo.isRejected = true;
      return;
    }

    if( is_accepted && !is_valid ) {
      setMessage( validation_result.message );
      data.validationInfo.isRejected = true;
      return;
    }

    if ( is_action_enter ) {
      if( ref_content !== validation_result.content ) setContent( validation_result.content );
      updateField( 'ENTER', id, data.Name, { Content: validation_result.content } );
      return;
    }
    
    if ( is_action_tab ) {
      if( ref_content !== validation_result.content ) setContent( validation_result.content );
      updateField( 'TAB', id, data.Name, { Content: validation_result.content } );
      return;
    }

    // === TABLE COMMANDS ===
    if( data.isColumn ) {
      const is_action_create_row_above = event.key === 'ArrowUp'   && event.ctrlKey;
      const is_action_create_row_below = event.key === 'ArrowDown' && event.ctrlKey;
      const is_action_remove_row       = event.key === 'Delete'    && event.ctrlKey;
      if( is_action_create_row_above || is_action_create_row_below) {
        addTableRow( { field_id: id, isAfter: is_action_create_row_below } );
        event.preventDefault();
        return;
      }
      if( is_action_remove_row ) {
        removeTableRow( { field_id: id } );
        event.preventDefault();
        return;
      }
    }
  }

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function getInputData() {
    //-----------------------------------------------------------------

    const STANDARD_PROPS = {
      name       : 'field_txt'   ,
      id         : id            ,
      style      : _styles.input ,
      field      : data          ,
      value      : content       ,
      has_focus  : has_focus     ,
      onFocus    : onFocus       ,
      onBlur     : onBlur        ,
      onKeyDown  : onKeyDown     ,
      inputRef   : setRefNode    ,
      setMessage : setMessage    ,
    }
  
    if( type === 'pagetype'  ) {
      input_validation = validate_pagetype;
      input_element = <Pagetype  { ...STANDARD_PROPS } />;
    }
    else if( type === 'doctype'   ) {
      input_validation = validate_doctype;
      input_element = <Doctype   { ...STANDARD_PROPS } />;
    }
    else if( type === 'selection' ) {
      input_validation = validate_selection;
      input_element = <Selection { ...STANDARD_PROPS } />;
    }
    else if( type === 'amount'    ) {
      input_validation = validate_amount;
      input_element = <Amount    { ...STANDARD_PROPS } />;
    }
    else if( type === 'date'      ) {
      input_validation = validate_date;
      input_element = <Date      { ...STANDARD_PROPS } />;
    }
    else if( selection_values.length > 0 ) {
      input_validation = validate_selection;
      input_element = <Selection { ...STANDARD_PROPS } />;
    }
    else {
      input_validation = validate_standard;
      input_element = <Standard  { ...STANDARD_PROPS } />;
    }
  }

  //#endregion

  function validate_common (content) {
    let isvalid = true;

    // LenOption
    const lenOpt = data?.DisplayInfo?.LenOption?.toLowerCase();
    if(lenOpt) {
      // Strict Len - content must match field length exactly
      if(lenOpt.includes('s') && content.length != length_char){
        isvalid = false;
        setMessage( `Invalid field length, must be exactly ${length_char} characters` );
      }
      // Max Len - content length must not exceed field length
      else if(lenOpt.includes('f') && content.length > length_char){
        isvalid = false;
        setMessage( `Invalid field length, Max ${length_char} characters` );
      }
    }

    return isvalid;
  }

  //#region [RENDER]
 
  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( data.Id === undefined ) return null;

    getInputData();
    return (
      <Box { ..._props.container }>
        <Box sx={_styles.header_container}>
          <CardHeader { ..._props.header  } action={displayPageNumber()}/>
        </Box>
        <Box { ..._props.content } >
          { input_element }
          { has_focus && message }
        </Box>
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displayPageNumber() {
    //-----------------------------------------------------------------
    if( !data.page_number ) return null;

    return <Box sx={_styles.page_number}>Page: {data.page_number}</Box>
  }

  //-----------------------------------------------------------------
  function displayContent() {
    //-----------------------------------------------------------------
    let result = null;

    const type = data.DisplayInfo.Type;

    const STANDARD_PROPS = {
      name            : 'field_txt'     ,
      id              : id              ,
      style           : _styles.input   ,
      field           : data            ,
      value           : content         ,
      has_focus       : has_focus       ,
      onFocus         : onFocus         ,
      onBlur          : onBlur          ,
      onKeyDown       : onKeyDown       ,
      inputRef        : setRefNode      ,
      setMessage      : setMessage      ,
    }

    if( type === 'pagetype' )               result = <Pagetype  { ...STANDARD_PROPS } />;
    else if( type ===  'doctype' )          result = <Doctype   { ...STANDARD_PROPS } />;
    else if( type ===  'selection' )        result = <Selection { ...STANDARD_PROPS } />;
    else if( type ===  'amount' )           result = <Amount    { ...STANDARD_PROPS } />;
    else if( type ===  'date' )             result = <Date      { ...STANDARD_PROPS } />;
    else if( selection_values.length > 0 )  result = <Selection { ...STANDARD_PROPS } />;
    else                                    result = <Standard  { ...STANDARD_PROPS } />;

    return result;
  }

  //#endregion

  return display();
}